import React, { createContext, useContext, useState } from 'react';
import styled from '@emotion/styled';
import { NavLink, Link } from 'react-router-dom';
import { colors, mediaQuery } from '@modernhealth/vital-foundation';
import { Button, ButtonClassNames, ComponentTextSmall, Icon } from '@modernhealth/vital-components';

import { ReactComponent as Wordmark } from 'src/assets/icons/wordmark.svg';

import { useTranslation } from '../hooks/formatMessage';
import { LocaleSelectionModal, LocaleSelector } from './LocaleSelector';

type CollapseBreakpoint = 'xs' | 'sm' | 'md' | 'lg';

/**
 * A context which stores whether the nav is opened or closed.
 */
const NavContext = createContext<{
  open: boolean;
  setOpen: (open: boolean) => void;
}>({
  open: false,
  setOpen: () => undefined,
});

/**
 * A context which stores the breakpoint for a collapsible group.
 */
const NavCollapseGroupContext = createContext<{
  breakpoint: CollapseBreakpoint;
}>({
  breakpoint: 'lg',
});

const NavCollapseContainer = styled.ul<{
  open: boolean;
  expand: boolean;
  breakpoint: CollapseBreakpoint;
}>(({ open, expand, breakpoint }) => ({
  margin: 0,
  padding: 0,
  flexWrap: 'wrap',
  height: 'auto',
  overflow: open ? 'auto' : undefined,
  display: 'flex',
  alignItems: 'center',
  flex: expand ? '1 1 100%' : '0 0 auto',
  [mediaQuery.maxWidth(breakpoint)]: {
    display: open ? 'flex' : 'none',
    order: 9999,
  },
}));

const NavCollapseGroup: React.FC<{ expand?: boolean; breakpoint?: CollapseBreakpoint }> = ({
  children,
  breakpoint = 'lg',
  expand = true,
}) => {
  const { open } = useContext(NavContext);

  return (
    <NavCollapseContainer
      css={!open ? { overflow: 'clip' } : ''}
      open={open}
      breakpoint={breakpoint}
      expand={expand}
    >
      <NavCollapseGroupContext.Provider value={{ breakpoint }}>
        {children}
      </NavCollapseGroupContext.Provider>
    </NavCollapseContainer>
  );
};

const NavGroup: React.FC<{ breakpoint?: CollapseBreakpoint }> = ({ children, breakpoint }) => {
  return (
    <ul
      css={{
        display: 'flex',
        alignItems: 'center',
        flex: '0 0 auto',
        ...(breakpoint
          ? {
              [mediaQuery.maxWidth(breakpoint)]: {
                display: 'none',
              },
            }
          : {}),
      }}
    >
      {children}
    </ul>
  );
};

const NavItemContainer = styled.li<{
  breakpoint?: CollapseBreakpoint;
  noPadding?: boolean;
}>(({ breakpoint = 'lg', noPadding = false }) => ({
  padding: noPadding ? undefined : '0.5rem 0',
  cursor: 'pointer',
  alignItems: 'center',
  display: 'flex',
  marginLeft: 30,
  [mediaQuery.maxWidth(breakpoint)]: {
    padding: noPadding ? undefined : '1rem 0',
    flexGrow: 1,
    flexBasis: '100%',
    marginLeft: 8,
  },
}));

const NavItemNoCollapseContainer = styled.li<{
  noPadding?: boolean;
}>(({ noPadding = false }) => ({
  padding: noPadding ? undefined : '0.5rem 0',
  cursor: 'pointer',
  alignItems: 'center',
  display: 'flex',
  marginLeft: 30,
}));

const NavItem: React.FC<{ noPadding?: boolean }> = ({ children, noPadding }) => {
  const context = useContext(NavCollapseGroupContext);

  if (!context) {
    return (
      <NavItemNoCollapseContainer noPadding={noPadding}>{children}</NavItemNoCollapseContainer>
    );
  }

  const { breakpoint = 'lg' } = context;

  return (
    <NavItemContainer breakpoint={breakpoint} noPadding={noPadding}>
      {children}
    </NavItemContainer>
  );
};

const NavLinkItem: React.FC<{ to: string; text: string }> = ({ to, text }) => {
  return (
    <NavItem>
      <NavLink
        to={to}
        exact
        css={{
          color: colors.gray65,
          '&:hover': {
            color: colors.blue55,
            textDecoration: 'underline',
          },
        }}
        activeStyle={{
          textDecoration: 'underline',
        }}
      >
        <ComponentTextSmall>{text}</ComponentTextSmall>
      </NavLink>
    </NavItem>
  );
};

const Wrapper = styled.nav<{ open: boolean }>(({ open }) => ({
  paddingTop: 20,
  paddingBottom: 20,
  display: 'flex',
  flexFlow: 'row',
  marginBottom: 60,
  alignItems: 'center',
  flexWrap: 'nowrap',
  justifyContent: 'space-between',
  [mediaQuery.maxWidth('lg')]: {
    marginBottom: 10,
    flexFlow: 'row',
    flexWrap: open ? 'wrap' : 'nowrap',
  },
}));

const NavSpacer = styled.div({ marginLeft: 'auto', height: 0 });

const NavbarToggleContainer = styled.button({
  display: 'none',
  background: 'none',
  border: 'none',
  color: colors.gray65,
  marginLeft: 0,
  marginRight: 10,
  width: 34,
  [mediaQuery.maxWidth('sm')]: {
    marginRight: 0,
  },
  [mediaQuery.maxWidth('lg')]: {
    display: 'block',
  },
});

const NavToggle = () => {
  const { open, setOpen } = useContext(NavContext);

  return (
    <NavbarToggleContainer onClick={() => setOpen(!open)}>
      {open ? <Icon size={20} name="x" /> : <Icon size={20} name="hamburger" />}
    </NavbarToggleContainer>
  );
};

const NavbarLogoLink = styled(Link)({
  padding: '0.25rem 0',
  marginRight: 90,
  [mediaQuery.maxWidth('md')]: {
    marginRight: 10,
  },
});

const NavBrand: React.FC<{ to: string }> = ({ to, children }) => {
  return <NavbarLogoLink to={to}>{children}</NavbarLogoLink>;
};

const NavSignIn = styled(ComponentTextSmall)`
  color: ${colors.gray85};
  white-space: nowrap;
  margin-left: 30px;
`;

const NavSignInLink = styled.a({
  color: colors.gray85,
  '&:hover': {
    color: colors.blue55,
  },
});
export const memberRegistrationUrl = `${process.env.REACT_APP_MEMBER_WEB_URL}/circles/`;

const Navigation: React.FC = () => {
  const { $t } = useTranslation();
  const [showLocaleModal, setShowLocaleModal] = useState(false);

  return (
    <>
      <NavToggle />
      <NavBrand to="/">
        <Wordmark />
      </NavBrand>
      <NavCollapseGroup>
        <NavLinkItem to="/upcoming" text={$t({ defaultMessage: 'Upcoming Circles' })} />
        <NavLinkItem to="/about-circles" text={$t({ defaultMessage: 'About Circles' })} />
        <NavLinkItem to="/code-of-conduct" text={$t({ defaultMessage: 'Community Guidelines' })} />
        <NavItemContainer
          css={{
            /* <Button /> does not support css={{}} directly yet */
            [`.${ButtonClassNames.ButtonContainer}`]: { padding: 0 },
            /* This item only appears on xs mobile views */
            [mediaQuery.minWidth('xs')]: { display: 'none' },
          }}
        >
          <Button
            title="Change Language"
            variant="flat"
            size="medium"
            onClick={() => {
              setShowLocaleModal(true);
            }}
          />
        </NavItemContainer>
      </NavCollapseGroup>
      <NavSpacer />
      <NavGroup breakpoint="xs">
        <NavItem noPadding>
          <LocaleSelector />
        </NavItem>
      </NavGroup>
      <NavSignIn>
        <NavSignInLink href={memberRegistrationUrl} target="_blank">
          {$t({ defaultMessage: 'Sign in' })}
        </NavSignInLink>
      </NavSignIn>
      <LocaleSelectionModal isVisible={showLocaleModal} close={() => setShowLocaleModal(false)} />
    </>
  );
};

const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <NavContext.Provider value={{ open, setOpen }}>
      <Wrapper open={open}>
        <Navigation />
      </Wrapper>
    </NavContext.Provider>
  );
};

export default Navbar;
